import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Grid2 as Grid, IconButton, Link, Stack, Toolbar, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { useDragDropManager } from 'react-dnd';
import Sticky from 'react-sticky-el/lib/basic-version';
import { useAppStore } from '../../app/store';
import { useCalendar } from '../calendar.util';
import MonthlyDay from '../monthly/monthly.day';
import pluralize from 'pluralize';

export default function WeeklyView(props) {
	const { user } = useAppStore();
	const unit = 'week';
	const theme = useTheme();

	const [toggleRender, setToggleRender] = useState(false);

	const dragDropManager = useDragDropManager();
	dragDropManager.monitor.subscribeToStateChange(() => {
		if (dragDropManager.monitor.didDrop()) {
			setToggleRender(!toggleRender);
		}
	});

	const getFirstDayOfWeek = (referenceDate) => {
		const startBasis = (+user.prefs.weeklyStartsOn || 0) == 1 ? 'isoWeek' : 'week';
		return moment(referenceDate).startOf(startBasis);
	}

	const getLastDayOfWeek = (referenceDate) => {
		const date = getFirstDayOfWeek(referenceDate);
		return moment(date).add(6, 'days');
	}

	const calendar = useCalendar(unit, getFirstDayOfWeek, getLastDayOfWeek);

	if ((+user.prefs.weeklyStartsOn || 0) != calendar.startDate.day()) {
		//Preference changed; update the calendar
		const change = ((+user.prefs.weeklyStartsOn || 0) - calendar.startDate.day());
		calendar.setCalendar(calendar.startDate.add(change, 'days'), true);
		return;
	}

	let weekendBgColour = theme.palette.background.weekend;

	let numHolidays = 0;
	const days = [];

	const nowrapSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: "ellipsis" };

	let date = moment(calendar.startDate);
	do {
		let dayOfWeek = date.day();
		let isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
		let isToday = date.isSame(new Date(), 'day');
		let isHoliday = calendar.isHoliday(date);
		numHolidays += isHoliday && !isWeekend ? 1 : 0;

		const bgColours = theme.palette.background;
		let bgColour = isToday ? bgColours.today : isWeekend ? bgColours.weekend : isHoliday ? bgColours.holiday : '';

		const dayTodos = _.filter(calendar.todos, { dueDate: date.valueOf() });

		if (!isWeekend || (user.prefs.weeklyIncludeWeekends == true)) {
			days.push(
				<Grid size={1} key={date} sx={{ p: 1, backgroundColor: bgColour, display: (!isWeekend || (user.prefs.weeklyIncludeWeekends == true)) ? 'initial' : 'none', minHeight: 100 }}>
					<Link href={"/calendar/daily?date=" + date} color="inherit">
						<Stack justifyContent="space-between" sx={{ mb: 1 }}>
							<Typography variant="h5" sx={nowrapSx}>{moment(date).format('dddd')}</Typography>
							<Typography variant="h5">{moment(date).format('D')}</Typography>
						</Stack>
					</Link>
					{isHoliday && <Typography variant="h5" color="textDisabled" sx={{ pt: 3, textAlign: 'center' }}>{calendar.getHoliday(date).name}</Typography>}
					<MonthlyDay date={date} todos={dayTodos} activities={calendar.filteredActivities} groupBy={user.prefs.focus} isHoliday={isHoliday} />
				</Grid>
			);
		}
		date = moment(date).add(1, 'day');
	} while (date.isSameOrBefore(calendar.endDate));

	const getRangeLabel = (date1, date2, format, formatIfSame, formatToCompare) => {
		if (date1.format(formatToCompare) == date2.format(formatToCompare)) {
			return date1.format(format) + ' - ' + date2.format(formatIfSame);
		}
		return date1.format(format) + ' - ' + date2.format(format);
	}

	const previousDate1 = moment(calendar.startDate).subtract(1, unit);
	const previousDate2 = moment(calendar.endDate).subtract(1, unit);
	const previousLabel = getRangeLabel(previousDate1, previousDate2, 'MMMM D', 'D', 'MMMM');

	const nextDate1 = moment(calendar.startDate).add(1, unit);
	const nextDate2 = moment(calendar.endDate).add(1, unit);
	const nextLabel = getRangeLabel(nextDate1, nextDate2, 'MMMM D', 'D', 'MMMM');

	const label = getRangeLabel(calendar.startDate, calendar.endDate, 'MMMM D', 'D', 'MMMM');
	const includesToday = moment().isAfter(calendar.startDate) && moment().isBefore(calendar.endDate);

	const gridBorder = {
		'--border': '2px solid #888',

		'& > div': {
			borderTop: 'var(--border)',
			borderBottom: 'var(--border)',
			borderRight: 'var(--border)',
		},
		'& > div:first-of-type': {
			borderLeft: 'var(--border)',
			borderTopLeftRadius: 4,
			borderBottomLeftRadius: 4,
		},
		'& > div:last-child': {
			borderTopRightRadius: 4,
			borderBottomRightRadius: 4,
		},
	};
	const dateSx = {
		'input': { display: 'none' },
		'fieldset': { border: 'none' },
		'.MuiInputAdornment-root': { margin: 0 },
	};

	return (
		<Box sx={{ p: 2 }}>
			<Sticky stickyStyle={{ backgroundColor: theme.palette.background.default, zIndex: 10, top: theme.mixins.toolbar["@media (min-width:600px)"].minHeight }} topOffset={-theme.mixins.toolbar["@media (min-width:600px)"].minHeight}>
				<Toolbar disableGutters sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
					<Box />

					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<IconButton onClick={() => calendar.moveCalendar(-1)}>
							<ChevronLeftIcon />
						</IconButton>
						<Stack spacing={0} direction="column" alignItems="center" sx={{ minWidth: 150 }}>
							<Typography variant="h5">{label}</Typography>
							{numHolidays > 0 && <Typography>{numHolidays} {pluralize('Day', numHolidays)} Break</Typography>}
						</Stack>
						<IconButton onClick={() => calendar.moveCalendar(1)}>
							<ChevronRightIcon />
						</IconButton>
					</Box>
					<Stack justifyContent="flex-end" alignItems="center" gap={0}>
						<DatePicker sx={dateSx} value={calendar.startDate || ''} onChange={(e) => calendar.setCalendar(e)} showDaysOutsideCurrentMonth
							slotProps={{
								day: (d) => {
									if (d.day.isBetween(calendar.startDate, calendar.endDate)) {
										return {
											sx: {
												backgroundColor: theme.palette.primary.main,
												color: theme.palette.primary.contrastText,
											}
										};
									}
								}
							}}
						/>
					</Stack>
				</Toolbar>
			</Sticky>

			<Box sx={{ my: 1, width: '100%' }}>
				<Grid container columns={(user.prefs.weeklyIncludeWeekends == true) ? 7 : 5} sx={gridBorder} spacing={0}>
					{days}
				</Grid>
			</Box>
			{calendar.fabControl}
		</Box>
	);
}